/*
  Photo viewer.

  Clicking a link inside a .kv-lightbox-group opens that group, starting at the
  clicked photo, in the dialog its own section renders (or the first dialog on
  the page when its section has none). The viewer then moves through that group
  only. Links are ordinary links to the full size file, so with this script
  missing or still loading a click simply opens the photo.

  How it moves:
  - Opening grows the photo out of the picture it was pressed on while the
    backdrop fades in, and closing shrinks it back. When that picture is not on
    screen the viewer fades instead; the store can also choose a plain fade or
    no movement at all.
  - The photos sit side by side in a strip with a gap between them. Dragging
    with the mouse or a finger moves the strip with the pointer, so the next
    photo slides in. A drag of more than a few pixels turns the page when it is
    let go, unless the pointer was already heading back; otherwise the strip
    settles back. A drag wraps round with three photos or more; with two, a
    drag past either end meets resistance. The arrows and the arrow keys
    slide the strip the same way and always wrap round. A press that comes
    while the strip slides takes it on from where it is seen; a press quicker
    than the strip can follow waits for it, and a key or button held down
    plays one whole slide after another.
  - Each photo is cut at the edges of its own place in the strip, so one left
    enlarged slides away without running over its neighbour, and is fitted
    again once out of sight.
  - A photo larger than the screen can be enlarged: with a mouse by clicking it
    or the zoom button, on a touch screen by tapping it twice or pinching. An
    enlarged photo is dragged to look around it, and the same click or double
    tap brings it back to the fitted size.
  - On a touch screen a single tap hides or shows the controls. Dragging a
    photo at its fitted size up or down closes the viewer, the backdrop fading
    as it goes. A turn of the wheel over a photo at its fitted size closes too;
    over an enlarged one it moves it about.
  - Escape closes, the arrow keys move, and the page behind stays where it was.
    Any key brings the controls back, and they never fade while the keyboard
    is on one of them. On closing, focus returns to the photo on the page.

  Settings (Theme settings > Photo viewer) arrive as data attributes on the
  dialog: data-open-effect (zoom, fade or none), data-speed (milliseconds for
  opening, closing, the arrows and zooming), data-tap-zoom (double or single:
  what zooms on a touch screen) and data-swipe-close. The words it shows or
  announces arrive the same way, already translated: data-text-position (with
  [current] and [total] markers), data-text-copied and data-text-not-copied.

  Movements are played with the Web Animations API against end positions that
  are written first, so a page that cannot draw frames at the moment - a tab
  in the background - still ends up in the right place.
*/

(() => {
  // Rendering the viewer snippet more than once on a page would run this file
  // more than once. Without this guard each run would add its own document
  // click listener and a single click would be handled several times.
  if (window.kvLightboxReady) return;
  window.kvLightboxReady = true;

  const GROUP = '.kv-lightbox-group';
  const ITEM = '.kv-lightbox__item';

  // The gap between two photos in the strip, as a share of the screen width.
  const SLIDE_GAP = 0.12;
  // How far a press travels before it is a drag rather than a tap, and which
  // way the drag goes is decided.
  const DRAG_START = 10;
  // Two taps within this time and this distance on each axis are a double tap.
  const DOUBLE_TAP_TIME = 300;
  const DOUBLE_TAP_DISTANCE = 25;
  // A sideways drag longer than this turns the page when let go...
  const SWIPE_MIN = 30;
  // ...unless its last stretch moved between these two distances, or moved
  // back the other way: the pointer was slowing to a stop or turning round.
  const FLICK_STILL = 10;
  const FLICK_ON = 20;
  // The pointer's position is sampled at most this often to measure a flick.
  const SAMPLE_TIME = 50;
  // A flick slower than this (pixels per millisecond) counts as no flick.
  const FLICK_MIN_SPEED = 0.1;
  // Settling after a drag takes between these, faster after a quicker flick.
  const SETTLE_MIN = 250;
  const SETTLE_MAX = 400;
  // A photo dragged up or down closes once the backdrop behind it has faded
  // below this strength, which it does over half the screen's height; the
  // controls step aside as soon as it drops below the second.
  const CLOSE_OPACITY = 0.75;
  const CONTROLS_OPACITY = 0.95;
  // The largest a pinch may make a photo, as a multiple of its file's size.
  const MAX_ZOOM = 1.33;
  // Past an edge, a drag moves this share of the pointer's movement.
  const RESISTANCE = 0.35;
  // An enlarged photo let go mid-flick carries on for about this long.
  const PAN_CARRY = 195;
  // With a mouse, the controls fade two to four seconds after it stops.
  const IDLE_STEP = 2000;
  // On a touch screen up to this wide the bar lies over the photo instead of
  // keeping room for itself.
  const BARS_OVER_SCREEN = 1200;
  // A movement whose end is never reported is ended this long after it should
  // have finished, counted from its first frame, so a late first frame does
  // not cut it short...
  const MOVE_GRACE = 40;
  // ...and one that never gets a first frame at all - a page in the
  // background draws none - is ended this long after it should have finished.
  const MOVE_NO_FRAMES = 1000;
  // The longest a newly arrived photo waits for the browser to report it
  // decoded before the small copy behind it is taken away anyway - long
  // enough for a slow device to finish, short enough that a browser which
  // never reports it does not leave the copy there for good.
  const DECODE_WAIT = 3000;

  // Curves: the shared one for opening, closing and the controls; a gentle
  // start and end for zooming; a quick start and soft landing for settling.
  const EASE = 'cubic-bezier(0.4, 0, 0.22, 1)';
  const EASE_ZOOM = 'cubic-bezier(0.37, 0, 0.63, 1)';
  const EASE_SETTLE = 'cubic-bezier(0.33, 1, 0.68, 1)';
  const EASE_BOUNCE = 'cubic-bezier(0.61, 1, 0.88, 1)';

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const px = (value, fallback) => {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  };

  class KvLightbox {
    constructor(dialog) {
      this.dialog = dialog;
      this.frame = dialog.querySelector('[data-kv-lightbox-frame]');
      this.backdrop = dialog.querySelector('[data-kv-lightbox-backdrop]');
      this.viewport = dialog.querySelector('[data-kv-lightbox-viewport]');
      this.track = dialog.querySelector('[data-kv-lightbox-track]');
      this.bar = dialog.querySelector('.kv-lightbox__bar');
      this.navs = Array.from(dialog.querySelectorAll('.kv-lightbox__nav'));
      this.counter = dialog.querySelector('[data-kv-lightbox-counter]');
      this.status = dialog.querySelector('[data-kv-lightbox-status]');
      this.zoomButton = dialog.querySelector('[data-kv-lightbox-zoom]');

      const data = dialog.dataset;
      this.effect = ['zoom', 'fade', 'none'].includes(data.openEffect) ? data.openEffect : 'zoom';
      this.speed = clamp(px(data.speed, 330), 0, 2000);
      this.tapZoom = data.tapZoom === 'single' ? 'single' : 'double';
      this.swipeClose = data.swipeClose !== 'false';
      this.texts = {
        position: data.textPosition || '[current] / [total]',
        copied: data.textCopied || '',
        notCopied: data.textNotCopied || ''
      };

      this.items = [];
      this.index = 0;
      this.offset = 0;
      this.pointers = new Map();
      // Fingers that joined a gesture they cannot take part in, left out of it
      // until they lift.
      this.ignored = new Set();
      this.gesture = null;
      this.moves = new Set();
      // Counts every step between photos, so the end of an old step can tell
      // it has been overtaken by a newer one.
      this.stepToken = 0;
      // The press waiting for the step under way to end: 1 for the next
      // photo, -1 for the previous one, 0 for none.
      this.queued = 0;
      this.tapTimer = 0;
      this.lastTap = null;
      this.idleTimer = 0;
      this.idleCount = 0;
      this.state = 'closed';
      // Counts openings and tear-downs, so a step left waiting from one
      // opening never acts on the next.
      this.session = 0;

      // Three frames are made once and reused for every photo: the one on
      // show and its neighbours either side.
      this.slides = [0, 1, 2].map(() => this.makeSlide());

      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);

      this.wire('[data-kv-lightbox-close]', () => this.close());
      this.wire('[data-kv-lightbox-prev]', () => this.go(-1));
      this.wire('[data-kv-lightbox-next]', () => this.go(1));
      this.wire('[data-kv-lightbox-fullscreen]', () => this.toggleFullscreen());
      this.wire('[data-kv-lightbox-share]', () => this.share());
      this.wire('[data-kv-lightbox-zoom]', () => this.toggleZoom(null, 'mouse'));

      // Full screen is asked of the frame inside the dialog. Browsers refuse it
      // for a dialog itself, and a full screen page would be drawn over the
      // open dialog. Where the browser offers no full screen at all, as on
      // iPhone, the button would do nothing, so it is not shown.
      const fullscreenButton = dialog.querySelector('[data-kv-lightbox-fullscreen]');
      if (fullscreenButton && !(document.fullscreenEnabled && this.frame && this.frame.requestFullscreen)) {
        fullscreenButton.hidden = true;
      }

      if (this.viewport) {
        this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event, false));
        this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event, true));
        this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
        // The browser's own picture dragging would take the gesture away.
        this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
      }

      // With a mouse the controls come back whenever it moves, and fade again
      // once it has rested.
      this.dialog.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'mouse') this.wake();
      });

      // A finger on a faded control only brings the controls back: it cannot
      // see what it is pressing, so the press itself does nothing. A resting
      // mouse still presses them, as it points at them on purpose.
      this.swallowClick = false;
      this.dialog.addEventListener(
        'pointerdown',
        (event) => {
          this.swallowClick = false;
          if (event.pointerType === 'mouse' || !this.dialog.classList.contains('kv-lightbox--idle')) return;
          if (!event.target.closest('.kv-lightbox__bar, .kv-lightbox__nav')) return;
          this.swallowClick = true;
          this.reveal();
        },
        true
      );
      this.dialog.addEventListener(
        'click',
        (event) => {
          if (!this.swallowClick) return;
          this.swallowClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      // Escape asks the dialog to close; the viewer does it with the same
      // movement as the close button. Anything else that closes the dialog
      // still leaves the page as it was.
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      // A close event that arrives late - after the viewer has already been
      // opened again - belongs to the earlier opening and is ignored.
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.teardown();
      });
    }

    wire(selector, handler) {
      const element = this.dialog.querySelector(selector);
      if (element) element.addEventListener('click', handler);
    }

    makeSlide() {
      const slide = document.createElement('div');
      slide.className = 'kv-lightbox__slide';
      const zoom = document.createElement('div');
      zoom.className = 'kv-lightbox__zoom';
      const picture = document.createElement('img');
      picture.className = 'kv-lightbox__picture';
      picture.alt = '';
      picture.draggable = false;
      picture.decoding = 'async';
      zoom.appendChild(picture);
      slide.appendChild(zoom);
      if (this.track) this.track.appendChild(slide);

      const state = { el: slide, zoom, picture, item: -1, src: '', fit: null, scale: 1, x: 0, y: 0, needsFit: false };
      picture.addEventListener('load', () => this.onPictureLoad(state));
      picture.addEventListener('error', () => this.onPictureError(state));
      return state;
    }

    get current() {
      return this.slides[1];
    }

    get duration() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : this.speed;
    }

    // Other movements keep the pace of the chosen speed.
    paced(ms) {
      return this.duration ? Math.round((ms * this.duration) / 333) : 0;
    }

    get count() {
      return this.items.length;
    }

    // Wrapping round from the last photo to the first only makes sense with
    // at least three; with two, a drag past either end meets resistance.
    get loops() {
      return this.count >= 3;
    }

    /* ---------------------------------------------------------------------
       Opening and closing
       --------------------------------------------------------------------- */

    open(items, index, group) {
      // A dialog shut by other code, whose close event has not come through
      // yet - a page that is not being drawn holds it back - is put to rest
      // first, so the viewer can open again.
      if (this.state !== 'closed' && !this.dialog.open) this.teardown();
      if (this.state !== 'closed') return;
      this.session += 1;
      const session = this.session;
      this.items = items;
      this.group = group;
      this.index = clamp(index, 0, items.length - 1);
      this.state = 'opening';
      // Where focus was, for the rare photo that has nothing on the page to
      // hand focus back to when the viewer closes.
      this.opener = document.activeElement;

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('kv-lightbox-open');
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);

      this.dialog.classList.toggle('kv-lightbox--single', items.length < 2);
      this.setUiHidden(false);
      // Where the screen is mainly touched the controls stay until tapped
      // away, and start fading on their own only once a mouse is moved.
      if (!window.matchMedia('(hover: none)').matches) this.wake();
      this.measure();
      this.offset = 0;
      this.placeTrack();
      this.fillAll();
      this.updateCounter();
      this.emit('kv-lightbox:open');

      const slide = this.current;
      const duration = this.duration;
      const origin = this.effect === 'zoom' && duration ? this.originOf(this.index) : null;
      const moves = [];

      if (origin) {
        // The picture on the page makes way while its larger copy grows out of
        // it, so the photo never appears twice. The copy starts as exactly the
        // part of the picture that shows on the page. The cut carries the
        // corner rounding on the way, so the frame's own rounding is set aside
        // until the movement is over.
        this.hideOrigin(origin.element);
        const pose = this.originPose(slide, origin, 0);
        const rest = this.fullClip(slide);
        slide.zoom.style.borderRadius = '0';
        moves.push(
          this.animate(
            slide.zoom,
            [
              { transform: pose.transform, clipPath: pose.clip },
              { transform: this.transformOf(slide), clipPath: rest }
            ],
            duration
          ),
          this.animate(this.backdrop, [{ opacity: 0 }, { opacity: 1 }], duration),
          ...this.animateControls(0, 1, duration)
        );
      } else if (this.effect !== 'none' && duration) {
        // The photo is in place at once and the backdrop and the controls fade
        // in around it.
        moves.push(
          this.animate(this.backdrop, [{ opacity: 0 }, { opacity: 1 }], duration),
          ...this.animateControls(0, 1, duration)
        );
      }

      this.afterMoves(moves, () => {
        if (session !== this.session) return;
        // A close that cut the opening short has set the frame up for its own
        // movement; the frame's rounding is left to it.
        if (this.state !== 'closing') slide.zoom.style.borderRadius = '';
        if (this.state !== 'opening') return;
        this.state = 'open';
        this.showOrigin();
        // A file that arrived during the opening, in a shape other than the
        // one its link announced, is fitted again now the movement is over.
        this.refitWaiting();
        this.updateZoomState();
      });
    }

    close() {
      if (this.state === 'closed' || this.state === 'closing') return;
      this.state = 'closing';
      this.cancelTap();
      // Whatever was moving stops where it is seen. A step between photos that
      // was under way is completed for the photos' places only: nothing new is
      // loaded or announced on the way out.
      this.stopMoves(true, true);
      this.releaseAll();

      // The page hears first, so a gallery can bring the same photo to the
      // front for the viewer to shrink into.
      this.emit('kv-lightbox:close');

      const slide = this.current;
      const duration = this.duration;
      // The picture on the page may still be hidden by an opening this close
      // cut short; it is measured as the page shows it. In full screen the
      // page is not behind the viewer to shrink back into, so it fades.
      this.showOrigin();
      const origin =
        this.effect === 'zoom' && duration && slide.fit && !document.fullscreenElement ? this.originOf(this.index) : null;
      const backdropFrom = px(getComputedStyle(this.backdrop).opacity, 1);
      const moves = [];

      if (origin) {
        this.hideOrigin(origin.element);
        // The strip may be off its rest position - half way to the next photo,
        // or held by a finger - so the photo shrinks from where it is seen to
        // exactly the part of the picture that shows on the page.
        const pose = this.originPose(slide, origin, this.offset);
        const from = { transform: this.transformOf(slide), clipPath: slide.zoom.style.clipPath || this.fullClip(slide) };
        slide.zoom.style.borderRadius = '0';
        // Its place in the strip cuts whatever runs over its edges, and the
        // picture on the page may lie outside it, so the photo is let out of
        // its place on the way there.
        slide.el.style.overflow = 'visible';
        slide.zoom.style.transform = pose.transform;
        slide.zoom.style.clipPath = pose.clip;
        moves.push(this.animate(slide.zoom, [from, { transform: pose.transform, clipPath: pose.clip }], duration));
        this.backdrop.style.opacity = '0';
        moves.push(this.animate(this.backdrop, [{ opacity: backdropFrom }, { opacity: 0 }], duration));
        moves.push(...this.animateControls(1, 0, duration));
        // A neighbour still partly in sight fades out with the backdrop.
        this.slides.forEach((other) => {
          if (other === slide || other.el.hidden) return;
          other.el.style.opacity = '0';
          moves.push(this.animate(other.el, [{ opacity: 1 }, { opacity: 0 }], duration));
        });
      } else if (this.effect !== 'none' && duration) {
        this.frame.style.opacity = '0';
        moves.push(this.animate(this.frame, [{ opacity: 1 }, { opacity: 0 }], duration));
      }

      const session = this.session;
      this.afterMoves(moves, () => {
        if (session !== this.session) return;
        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
        // Not every engine fires the dialog's close event dependably, and the
        // cost of missing it is a page that can no longer be scrolled.
        this.teardown();
      });
    }

    /* Safe to run more than once: every step below is a reset. */
    teardown() {
      const wasOpen = this.state !== 'closed';
      this.state = 'closed';
      this.session += 1;
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      document.documentElement.classList.remove('kv-lightbox-open');
      clearInterval(this.idleTimer);
      this.idleTimer = 0;
      this.cancelTap();
      // A step between photos still under way is dropped, not completed: the
      // viewer is gone, so nothing is loaded or announced any more.
      this.dropStep();
      this.stopMoves(false);
      this.releaseAll();
      this.showOrigin();

      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});

      this.frame.style.opacity = '';
      this.backdrop.style.opacity = '';
      [this.bar, ...this.navs].forEach((element) => {
        if (!element) return;
        element.style.opacity = '';
        element.style.transition = '';
      });
      this.dialog.classList.remove(
        'kv-lightbox--zoomed',
        'kv-lightbox--zoomable',
        'kv-lightbox--dragging',
        'kv-lightbox--idle',
        'kv-lightbox--ui-hidden'
      );
      // The zoom control is offered again only once the next photo has shown
      // it can be enlarged, so the next opening never puts focus on a control
      // that then disappears.
      if (this.zoomButton) {
        this.zoomButton.hidden = true;
        this.zoomButton.setAttribute('aria-pressed', 'false');
      }

      // Focus goes back to the photo on the page, rather than to the top of
      // the page, which is where the browser would leave it otherwise.
      const target = wasOpen ? this.focusTarget() : null;

      // Dropping the sources frees the decoded files and guarantees the next
      // opening cannot flash the photos of this one.
      this.slides.forEach((slide) => {
        slide.picture.removeAttribute('src');
        slide.src = '';
        slide.item = -1;
        slide.needsFit = false;
        slide.failed = false;
        slide.el.classList.remove('kv-lightbox__slide--loading');
        slide.el.style.opacity = '';
        slide.el.style.overflow = '';
        slide.zoom.style.backgroundImage = '';
        slide.zoom.style.clipPath = '';
        slide.zoom.style.borderRadius = '';
      });

      if (target) {
        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          target.focus();
        }
      }
    }

    /*
      Runs a step once the given movements are over, however each of them
      ends: played out, stopped by a press, or ended by its timer. With no
      movement at all the step runs at once.
    */
    afterMoves(moves, step) {
      const playing = moves.filter(Boolean);
      if (!playing.length) {
        step();
        return;
      }
      Promise.all(playing.map((entry) => entry.over)).then(step);
    }

    /*
      Where focus goes when the viewer closes: the control the link names, the
      link itself when shoppers can reach it, or whatever had focus when the
      viewer opened. The photo on show counts, not the one first pressed, as a
      gallery moves to that photo on closing.
    */
    focusTarget() {
      const link = this.items[this.index];
      const usable = (element) =>
        element &&
        element.isConnected &&
        element !== document.body &&
        !this.dialog.contains(element) &&
        typeof element.focus === 'function';
      // A tile scrolled out of a row that did not follow the viewer is left
      // alone: focus on it could not be seen.
      const seen = (element) => usable(element) && this.inSight(element).some;
      if (link) {
        const selector = link.dataset.kvLightboxFocus;
        if (selector) {
          let named = null;
          try {
            named = document.querySelector(selector);
          } catch (error) {
            named = null;
          }
          if (seen(named)) return named;
        }
        if (seen(link) && link.tabIndex >= 0 && link.getAttribute('aria-hidden') !== 'true') return link;
      }
      return usable(this.opener) ? this.opener : null;
    }

    /*
      How much of an element shows: its box cut down by every box around it
      that hides what overflows it, such as a scrolling row of tiles. Some is
      whether any of it shows on the screen; whole is whether none of it is
      cut off by those boxes (the edge of the screen does not count, as a
      photo moving past it stays in view the whole way).
    */
    inSight(element) {
      const rect = element.getBoundingClientRect();
      let left = rect.left;
      let top = rect.top;
      let right = rect.right;
      let bottom = rect.bottom;
      for (let node = element.parentElement; node && node !== document.documentElement; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.overflowX === 'visible' && style.overflowY === 'visible' && style.clipPath === 'none') continue;
        const box = node.getBoundingClientRect();
        left = Math.max(left, box.left);
        top = Math.max(top, box.top);
        right = Math.min(right, box.right);
        bottom = Math.min(bottom, box.bottom);
      }
      const onScreen =
        Math.min(right, window.innerWidth) - Math.max(left, 0) > 4 &&
        Math.min(bottom, window.innerHeight) - Math.max(top, 0) > 4;
      const whole =
        left - rect.left < 2 && top - rect.top < 2 && rect.right - right < 2 && rect.bottom - bottom < 2;
      return { rect, some: right - left > 4 && bottom - top > 4 && onScreen, whole };
    }

    // The picture on the page that stands for a photo: the one named by its
    // link, or the one inside it.
    originElement(index) {
      const link = this.items[index];
      if (!link) return null;
      let element = null;
      const selector = link.dataset.kvLightboxOrigin;
      if (selector) {
        try {
          element = document.querySelector(selector);
        } catch (error) {
          element = null;
        }
      }
      return element || link.querySelector('img');
    }

    /*
      The picture a photo grows out of and shrinks back into, and the part of
      the screen it takes up. Only a picture actually on screen counts, and
      only whole: one partly hidden by a row scrolled past it would have the
      photo grow out of, or shrink into, a place where nothing is drawn.

      A picture shown whole inside a larger box (object-fit: contain) is only
      as big as its drawing; the empty rest of the box is not grown from. A
      picture cropped to its box (object-fit: cover) shows only the box, so
      the box is what the photo starts from and returns to.
    */
    originOf(index) {
      const element = this.originElement(index);
      if (!element) return null;

      const style = getComputedStyle(element);
      if (style.visibility === 'hidden' || style.display === 'none') return null;
      const sight = this.inSight(element);
      if (!sight.some || !sight.whole) return null;
      const rect = sight.rect;

      let { left, top, width, height } = rect;
      const slide = this.slides.find((candidate) => candidate.item === index) || this.current;
      const ratio = slide.fit ? slide.fit.width / slide.fit.height : width / height;
      let fills = true;
      if (style.objectFit === 'contain' && ratio > 0) {
        if (width / height > ratio) {
          const drawn = height * ratio;
          fills = rect.width - drawn < 2;
          left += (width - drawn) / 2;
          width = drawn;
        } else {
          const drawn = width / ratio;
          fills = rect.height - drawn < 2;
          top += (height - drawn) / 2;
          height = drawn;
        }
      }
      const frame = this.frame.getBoundingClientRect();
      return {
        element,
        rect: { x: left - frame.left, y: top - frame.top, width, height },
        // Rounded corners only belong to a drawing that fills its box.
        radius: fills ? this.radiusOf(element) : 0
      };
    }

    /*
      The corner rounding a picture shows on the page: its own, or that of a
      box around it of the same size that clips it, as a gallery tile does.
    */
    radiusOf(element) {
      const size = element.getBoundingClientRect();
      let node = element;
      for (let depth = 0; node && depth < 3; depth += 1) {
        const box = node.getBoundingClientRect();
        if (Math.abs(box.width - size.width) > 2 || Math.abs(box.height - size.height) > 2) break;
        const value = getComputedStyle(node).borderTopLeftRadius;
        const radius = value.endsWith('%') ? (parseFloat(value) / 100) * box.width : parseFloat(value);
        if (radius > 0) return radius;
        node = node.parentElement;
      }
      return 0;
    }

    /*
      How a photo's frame lies exactly over a picture on the page: the whole
      photo scaled to cover the picture's box, centred on it, and cut down to
      that box. The cut is given in the frame's own, unscaled, pixels. Offset
      is where the strip stands, which moves the frame along with it.
    */
    originPose(slide, origin, offset) {
      const { rect, radius } = origin;
      const fit = slide.fit;
      const scale = Math.max(rect.width / fit.width, rect.height / fit.height);
      const x = rect.x - offset + (rect.width - fit.width * scale) / 2;
      const y = rect.y + (rect.height - fit.height * scale) / 2;
      const cutX = Math.max((fit.width - rect.width / scale) / 2, 0);
      const cutY = Math.max((fit.height - rect.height / scale) / 2, 0);
      return {
        transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
        clip: `inset(${cutY}px ${cutX}px ${cutY}px ${cutX}px round ${radius / scale}px)`
      };
    }

    // The same cut, uncovering the whole photo with the corners it rests with.
    fullClip(slide) {
      const radius = px(getComputedStyle(slide.zoom).borderTopLeftRadius, 0);
      return `inset(0px 0px 0px 0px round ${radius}px)`;
    }

    hideOrigin(element) {
      this.showOrigin();
      this.hiddenOrigin = element;
      element.style.visibility = 'hidden';
    }

    showOrigin() {
      if (!this.hiddenOrigin) return;
      this.hiddenOrigin.style.visibility = '';
      this.hiddenOrigin = null;
    }

    // A small copy of a photo that the page has already loaded, to stand in
    // for it until its own file arrives; none when the page has not.
    thumbnailOf(index) {
      const link = this.items[index];
      const candidates = [this.originElement(index), link && link.querySelector('img')];
      for (const image of candidates) {
        if (image && image.tagName === 'IMG' && image.complete && image.naturalWidth > 0) {
          return image.currentSrc || image.src;
        }
      }
      return '';
    }

    /* ---------------------------------------------------------------------
       Layout
       --------------------------------------------------------------------- */

    /*
      The screen size and the room kept around the photo for the controls. On
      a phone or tablet the bar lies over the photo and hides when tapped, so
      the photo may use the full height there; the bar is then shaded darker.
    */
    measure() {
      const style = getComputedStyle(this.dialog);
      const over =
        window.matchMedia('(hover: none) and (pointer: coarse)').matches && window.screen.width <= BARS_OVER_SCREEN;
      this.dialog.classList.toggle('kv-lightbox--bars-over', over);
      this.width = this.frame.clientWidth;
      this.height = this.frame.clientHeight;
      this.padY = over ? 0 : px(style.getPropertyValue('--kv-lightbox-pad'), 44);
      this.padX = px(style.getPropertyValue('--kv-lightbox-inline'), 0);
      this.pitch = this.width + Math.round(this.width * SLIDE_GAP);
    }

    placeTrack() {
      if (this.track) this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`;
    }

    // Which photo a frame beside the current one shows, or -1 for none: past
    // either end of a set that does not wrap.
    neighbour(step) {
      const target = this.index + step;
      if (!this.loops && (target < 0 || target >= this.count)) return -1;
      return (target + this.count) % this.count;
    }

    // Loads the photo on show and its neighbours into the three frames.
    fillAll() {
      this.placeSlides();
      this.slides.forEach((slide, position) => {
        const index = position === 1 ? this.index : this.neighbour(position - 1);
        slide.el.hidden = index < 0;
        if (index >= 0) this.fill(slide, index);
      });
      this.updateZoomState();
    }

    // Puts the three frames side by side, the middle one at the strip's start.
    placeSlides() {
      this.slides.forEach((slide, position) => {
        slide.el.style.transform = `translate3d(${(position - 1) * this.pitch}px, 0, 0)`;
      });
    }

    fill(slide, index) {
      const link = this.items[index];
      if (!link) return;
      if (slide.item !== index || slide.src !== link.href) {
        slide.item = index;
        slide.src = link.href;
        slide.loaded = false;
        slide.failed = false;
        // A picture keeps drawing the file it had until the next one has
        // arrived, and the frame is about to take the new photo's shape. So
        // the old file is dropped and the picture kept out of sight until the
        // new one is ready; meanwhile the frame shows the small copy already
        // on the page, or stays empty.
        slide.picture.removeAttribute('src');
        slide.el.classList.add('kv-lightbox__slide--loading');
        const thumbnail = this.thumbnailOf(index);
        slide.zoom.style.backgroundImage = thumbnail ? `url("${thumbnail.replace(/"/g, '%22')}")` : '';
        const image = link.querySelector('img');
        slide.picture.alt = (image && image.alt) || '';
        slide.picture.src = link.href;
        this.fit(slide);
        return;
      }
      // A frame keeping its photo keeps its size too. One still enlarged - it
      // may yet be in sight beside the strip - is fitted once all is at rest.
      if (Math.abs(slide.scale - 1) > 0.001) slide.needsFit = true;
      else this.fit(slide);
    }

    // The size of the file as it will actually arrive, falling back to the
    // size the link announces until it has.
    naturalSize(slide) {
      const picture = slide.picture;
      if (slide.loaded && picture.naturalWidth > 0) {
        return { width: picture.naturalWidth, height: picture.naturalHeight };
      }
      const link = this.items[slide.item];
      const width = link ? parseInt(link.dataset.width, 10) : NaN;
      const height = link ? parseInt(link.dataset.height, 10) : NaN;
      if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        return { width, height };
      }
      return { width: this.width, height: Math.max(this.height - 2 * this.padY, 1) };
    }

    /*
      Fits a photo into the room between the controls, never larger than its
      file, centred, and sets it back to that fitted size.
    */
    fit(slide) {
      const natural = this.naturalSize(slide);
      const roomWidth = Math.max(this.width - 2 * this.padX, 1);
      const roomHeight = Math.max(this.height - 2 * this.padY, 1);
      const ratio = Math.min(roomWidth / natural.width, roomHeight / natural.height, 1);
      const width = Math.round(natural.width * ratio);
      const height = Math.round(natural.height * ratio);
      slide.fit = {
        width,
        height,
        ratio,
        x: Math.round((this.width - width) / 2),
        y: Math.round(this.padY + (roomHeight - height) / 2)
      };
      slide.picture.style.width = `${width}px`;
      slide.picture.style.height = `${height}px`;
      slide.scale = 1;
      slide.x = slide.fit.x;
      slide.y = slide.fit.y;
      slide.needsFit = false;
      this.applyZoom(slide);
    }

    onPictureLoad(slide) {
      const picture = slide.picture;
      // Only the file this frame asked for last counts.
      if (!slide.src || picture.currentSrc !== slide.src) return;
      slide.loaded = true;
      slide.el.classList.remove('kv-lightbox__slide--loading');

      // The small copy stays behind the picture until the picture has been
      // decoded and drawn over it, so the frame never shows an empty moment in
      // between. A browser that does not report the decoding - a page that is
      // not being drawn - has it cleared after a while all the same.
      const clear = () => {
        if (picture.currentSrc === slide.src) slide.zoom.style.backgroundImage = '';
      };
      const decoded = typeof picture.decode === 'function' ? picture.decode() : Promise.resolve();
      Promise.race([decoded, new Promise((resolve) => setTimeout(resolve, DECODE_WAIT))]).then(clear, clear);

      // A file whose shape differs from what the link announced is fitted
      // again: at once, or as soon as nothing is moving and no one is holding
      // the photo.
      slide.needsFit = true;
      this.refitWaiting();
      if (slide === this.current) this.updateZoomState();
    }

    /*
      A file that could not be loaded. The small copy from the page, when
      there is one, stays in its place; otherwise the picture is shown as the
      browser draws a missing file, with its description. Either way there is
      nothing to enlarge.
    */
    onPictureError(slide) {
      if (!slide.src || slide.picture.getAttribute('src') !== slide.src) return;
      slide.failed = true;
      if (!slide.zoom.style.backgroundImage) slide.el.classList.remove('kv-lightbox__slide--loading');
      if (slide === this.current) this.updateZoomState();
    }

    // Fits again every photo that could not be fitted when it needed to be -
    // its file arrived mid-movement, or it was enlarged as the strip moved on -
    // now that nothing is moving. The photo on show, if the shopper has it
    // enlarged, waits until it is back at its fitted size.
    refitWaiting() {
      if (this.state === 'closed' || this.state === 'closing') return;
      if (this.gesture || this.moves.size || this.pendingStep !== undefined) return;
      this.slides.forEach((slide) => {
        if (!slide.needsFit || slide.item < 0) return;
        if (slide === this.current && Math.abs(slide.scale - 1) > 0.001) return;
        this.fit(slide);
      });
      this.updateZoomState();
    }

    transformOf(slide) {
      return `translate3d(${slide.x}px, ${slide.y}px, 0) scale(${slide.scale})`;
    }

    // Each photo's corners follow its own size, so one sliding in beside an
    // enlarged photo keeps its rounding the whole way.
    applyZoom(slide) {
      slide.zoom.style.transform = this.transformOf(slide);
      slide.el.classList.toggle('kv-lightbox__slide--zoomed', slide.scale > 1.01);
    }

    // Moves a photo to new numbers, playing the way there when asked to.
    moveSlide(slide, next, duration, easing) {
      const from = this.transformOf(slide);
      Object.assign(slide, next);
      this.applyZoom(slide);
      const to = this.transformOf(slide);
      if (duration && from !== to) this.animate(slide.zoom, [{ transform: from }, { transform: to }], duration, easing);
    }

    /* ---------------------------------------------------------------------
       Zoom
       --------------------------------------------------------------------- */

    // Scale factors are measured against the fitted size. "Full" is the file
    // at one of its pixels to one of the screen's.
    fullScale(slide) {
      const natural = this.naturalSize(slide);
      return slide.fit ? natural.width / slide.fit.width : 1;
    }

    // A photo whose file failed has nothing to enlarge, however it is asked.
    maxScale(slide) {
      if (slide.failed) return 1;
      return Math.max(this.fullScale(slide) * MAX_ZOOM, 1);
    }

    // Worth enlarging only when the file holds more than is shown, and has
    // actually been loaded or is still on its way.
    zoomable(slide) {
      return !slide.failed && this.fullScale(slide) > 1.01;
    }

    /*
      The size a click or a double tap enlarges to: the file at full size. A
      double tap on a photo already shown at most of its size goes a little
      further, so there is always something to see.
    */
    targetScale(slide, pointerType) {
      const full = this.fullScale(slide);
      if (pointerType === 'mouse') return full;
      return Math.min(slide.fit.ratio < 0.7 ? full : full * 1.33, this.maxScale(slide));
    }

    // Where the photo may sit at a given scale: centred while it fits the
    // room, otherwise anywhere that keeps the room covered.
    bounds(slide, scale) {
      const axis = (size, screen, pad) => {
        const room = screen - 2 * pad;
        if (size <= room) {
          const centred = Math.round(pad + (room - size) / 2);
          return [centred, centred];
        }
        return [Math.round(screen - pad - size), pad];
      };
      const [minX, maxX] = axis(slide.fit.width * scale, this.width, this.padX);
      const [minY, maxY] = axis(slide.fit.height * scale, this.height, this.padY);
      return { minX, maxX, minY, maxY };
    }

    clampPan(slide, scale, x, y) {
      const b = this.bounds(slide, scale);
      return { x: clamp(x, b.minX, b.maxX), y: clamp(y, b.minY, b.maxY) };
    }

    // Scales a photo around a point on the screen, keeping that point under
    // the pointer, and keeps it inside its bounds.
    zoomAround(slide, scale, point, duration, easing) {
      const factor = scale / slide.scale;
      const pan = this.clampPan(
        slide,
        scale,
        point.x - (point.x - slide.x) * factor,
        point.y - (point.y - slide.y) * factor
      );
      this.moveSlide(slide, { scale, x: pan.x, y: pan.y }, duration, easing);
      this.updateZoomState();
      // Back at its fitted size without a movement to wait for, a photo whose
      // file arrived while it was enlarged is fitted to that file now.
      this.refitWaiting();
    }

    toggleZoom(point, pointerType) {
      const slide = this.current;
      if (!slide.fit || this.state !== 'open') return;
      // A photo still on its way somewhere zooms from where it is seen.
      this.holdSlide(slide);
      const center = point || { x: this.width / 2, y: this.height / 2 };
      if (slide.scale > 1.01) {
        this.zoomAround(slide, 1, center, this.duration, EASE_ZOOM);
      } else if (!slide.failed && (this.zoomable(slide) || pointerType !== 'mouse')) {
        this.zoomAround(slide, this.targetScale(slide, pointerType), center, this.duration, EASE_ZOOM);
      }
    }

    // Stops a photo's own movement where it has got to and takes its numbers
    // from there.
    holdSlide(slide) {
      const running = Array.from(this.moves).filter((entry) => entry.element === slide.zoom);
      if (!running.length) return;
      const matrix = new DOMMatrixReadOnly(getComputedStyle(slide.zoom).transform);
      running.forEach((entry) => {
        entry.animation.cancel();
        entry.end();
      });
      if (matrix.a) {
        slide.scale = matrix.a;
        slide.x = matrix.m41;
        slide.y = matrix.m42;
      }
      this.applyZoom(slide);
    }

    updateZoomState() {
      const slide = this.current;
      const zoomed = slide.scale > 1.01;
      const zoomable = !!slide.fit && this.zoomable(slide);
      this.dialog.classList.toggle('kv-lightbox--zoomed', zoomed);
      this.dialog.classList.toggle('kv-lightbox--zoomable', zoomable && !zoomed);
      if (this.zoomButton) {
        this.zoomButton.hidden = !zoomable && !zoomed;
        this.zoomButton.setAttribute('aria-pressed', String(zoomed));
      }
    }

    /* ---------------------------------------------------------------------
       Moving between photos
       --------------------------------------------------------------------- */

    /*
      The arrows and the arrow keys: always wrap, and always slide. A press
      that comes while the strip is still sliding takes it on from where it is
      seen, so quick presses run on smoothly and end on the right photo. A
      press that finds the strip not yet past the photo it is leaving could
      only be taken on by standing the strip more than one photo's width off
      its rest position, beyond the three frames; it waits instead for the
      step under way to end. A press back the other way takes back the one
      still waiting.
    */
    go(direction) {
      // Not while a finger or the mouse holds the strip or the photo: the
      // press would fight the drag over where the strip goes.
      if (this.count < 2 || this.state !== 'open' || this.gesture) return;
      // One press waits at most. Any more are dropped, so a button held down
      // with Enter, which repeats its click many times a second, never builds
      // a backlog that keeps the strip sliding after it is let go.
      if (this.pendingStep !== undefined && (this.queued || this.overruns())) {
        this.queued = clamp(this.queued + direction, -1, 1);
        return;
      }
      this.stopMoves(true);
      this.restBackdrop();
      this.cancelTap();
      // In a set that does not wrap, the photo at the far end is brought into
      // the frame beside this one, so it can slide in all the same.
      const slide = this.slides[1 + direction];
      if (slide.el.hidden) {
        slide.el.hidden = false;
        this.fill(slide, (this.index + direction + this.count) % this.count);
      }
      this.settleTrack(direction, this.duration, EASE);
    }

    /*
      Slides the strip to the next photo (direction 1), the previous one (-1)
      or back to the current one (0), then moves the photos along so the one
      on show is in the middle frame again.
    */
    settleTrack(direction, duration, easing) {
      const from = this.offset;
      const to = -direction * this.pitch;
      this.offset = to;
      this.placeTrack();
      this.pendingStep = direction;
      // This step's own number: only its own end may complete it, never the
      // end of a step it overtook.
      this.stepToken += 1;
      const token = this.stepToken;
      const move =
        duration && from !== to
          ? this.animate(
              this.track,
              [{ transform: `translate3d(${from}px, 0, 0)` }, { transform: `translate3d(${to}px, 0, 0)` }],
              duration,
              easing,
              'track'
            )
          : null;
      if (direction) {
        // The counter changes as the strip starts to move, as the shopper
        // expects from the arrow they pressed.
        this.index = (this.index + direction + this.count) % this.count;
        this.updateCounter();
      }
      this.afterMoves([move], () => {
        if (token !== this.stepToken) return;
        this.commitStep(false);
        // The strip is at rest. A frame past either end of a set that does not
        // wrap, brought in for a slide that was then taken back, is put away
        // again, and frames left enlarged on the way, out of sight now, go back
        // to their fitted size.
        if (!direction) {
          this.slides.forEach((slide, position) => {
            if (position !== 1 && this.neighbour(position - 1) < 0) slide.el.hidden = true;
          });
        }
        this.refitWaiting();
        // A press that waited for this step plays now, as a whole slide from
        // rest; any still behind it wait for that one in turn.
        if (this.queued) {
          const next = Math.sign(this.queued);
          this.queued -= next;
          this.go(next);
        }
      });
    }

    // Where the strip is seen now, part way through a slide or at rest.
    seenOffset() {
      const sliding = Array.from(this.moves).some((entry) => entry.kind === 'track');
      return sliding && this.track ? new DOMMatrixReadOnly(getComputedStyle(this.track).transform).m41 : this.offset;
    }

    /*
      Whether the step under way has yet to bring the strip past the photo it
      is leaving. The photo it is heading for is then still wholly out of
      sight, and carrying the strip on from where it is seen would stand it
      more than one photo's width off its rest position.
    */
    overruns() {
      const direction = this.pendingStep;
      return !!direction && Math.abs(this.seenOffset() + direction * this.pitch) > this.pitch;
    }

    /*
      Puts the strip back at rest with the photo on show in the middle frame.
      Quiet only moves the frames round: nothing is loaded or announced, as
      when the viewer is closing. Carried means the strip is about to be put
      back where it was seen, so the photo that left the middle is still in
      sight.
    */
    commitStep(quiet, carried) {
      const direction = this.pendingStep;
      if (direction === undefined) return;
      this.pendingStep = undefined;
      this.stepToken += 1;
      this.cancelMoves('track');
      if (direction) {
        if (direction > 0) this.slides.push(this.slides.shift());
        else this.slides.unshift(this.slides.pop());
        // The photo that left the middle goes back to its fitted size - once
        // it is out of sight, if it can still be seen now.
        const leaving = this.slides[1 - direction];
        if (leaving && leaving.fit) {
          if (carried) leaving.needsFit = true;
          else this.fit(leaving);
        }
        this.offset = 0;
        this.placeTrack();
        if (quiet) {
          this.placeSlides();
        } else {
          this.fillAll();
          this.emit('kv-lightbox:change');
        }
      } else {
        this.offset = 0;
        this.placeTrack();
      }
      this.updateZoomState();
    }

    // Forgets a step still under way without completing it, and the presses
    // waiting for it.
    dropStep() {
      this.pendingStep = undefined;
      this.stepToken += 1;
      this.queued = 0;
    }

    updateCounter() {
      const position = this.texts.position
        .replace('[current]', String(this.index + 1))
        .replace('[total]', String(this.count));
      if (this.counter) this.counter.textContent = position;
      if (this.status) this.status.textContent = position;
    }

    /* ---------------------------------------------------------------------
       Gestures
       --------------------------------------------------------------------- */

    point(event) {
      const frame = this.frame.getBoundingClientRect();
      return { x: event.clientX - frame.left, y: event.clientY - frame.top };
    }

    newGesture(point, time, type) {
      const slide = this.current;
      return {
        mode: 'pending',
        type,
        origin: point,
        start: point,
        startTime: time,
        samples: [{ x: point.x, y: point.y, t: time }],
        sampleTime: time,
        startX: slide.x,
        startY: slide.y,
        startOffset: this.offset
      };
    }

    onPointerDown(event) {
      if (this.state !== 'open') return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // A finger that joins a gesture it cannot share - a slide between photos
      // already under way, or a third finger - is left out of it until it
      // lifts, so two fingers never pull the strip in turn.
      if (this.pointers.size >= 2 || (this.pointers.size === 1 && !this.canPinch())) {
        this.ignored.add(event.pointerId);
        return;
      }
      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        // A pointer that is already gone cannot be captured; nothing is lost.
      }
      // Whatever was still moving stops where it is and is taken from there.
      this.stopMoves(true);
      const point = this.point(event);
      this.pointers.set(event.pointerId, point);

      if (this.pointers.size === 1) {
        this.gesture = this.newGesture(point, event.timeStamp, event.pointerType);
      } else {
        this.startPinch(event.timeStamp);
      }
    }

    // A second finger turns the gesture into a pinch, unless the strip is on
    // its way to another photo: a pinch there would enlarge a photo that is
    // partly off the screen.
    canPinch() {
      return !!this.gesture && this.gesture.mode !== 'swipe' && this.offset === 0;
    }

    startPinch(time) {
      const slide = this.current;
      const [a, b] = Array.from(this.pointers.values());
      this.cancelTap();
      // A drag up or down may have hidden the controls on its way; the pinch
      // takes over from it, so they come back to what the shopper chose.
      this.setUiHidden(this.uiHidden);
      this.gesture = {
        mode: 'pinch',
        type: 'touch',
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        middle: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        scale: slide.scale,
        startX: slide.x,
        startY: slide.y,
        wentOver: false,
        closing: false,
        startTime: time
      };
      this.dialog.classList.add('kv-lightbox--dragging');
    }

    // The pointer's position every so often, the last three kept, to tell a
    // flick from a stop.
    sample(gesture, point, time) {
      if (time - gesture.sampleTime <= SAMPLE_TIME) return;
      gesture.samples.push({ x: point.x, y: point.y, t: time });
      if (gesture.samples.length > 3) gesture.samples.shift();
      gesture.sampleTime = time;
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      const point = this.point(event);
      this.pointers.set(event.pointerId, point);
      const gesture = this.gesture;
      const slide = this.current;

      if (gesture.mode === 'pinch') {
        this.movePinch(slide);
        return;
      }

      // The release itself is not a sample: the flick is measured up to it
      // from the samples taken on the way.
      if (event.type === 'pointermove') this.sample(gesture, point, event.timeStamp);

      if (gesture.mode === 'pending') {
        const dx = point.x - gesture.origin.x;
        const dy = point.y - gesture.origin.y;
        // The way a drag goes is settled once one axis leads the other by the
        // threshold; a drag running straight along the diagonal is settled
        // once it has gone twice as far.
        if (Math.abs(Math.abs(dx) - Math.abs(dy)) < DRAG_START && Math.hypot(dx, dy) < DRAG_START * 2) return;
        this.cancelTap();
        const sideways = Math.abs(dx) >= Math.abs(dy);
        if (slide.scale > 1.01) {
          gesture.mode = 'pan';
        } else if (sideways || this.offset !== 0) {
          gesture.mode = 'swipe';
        } else if (this.swipeClose) {
          gesture.mode = 'dismiss';
        } else {
          gesture.mode = 'none';
        }
        // Movement counts from where the pointer crossed the threshold, so the
        // photo does not jump by the distance it took to decide, and does not
        // fall behind when the pointer reports its moves far apart.
        const share = DRAG_START / Math.hypot(dx, dy);
        gesture.start = { x: gesture.origin.x + dx * share, y: gesture.origin.y + dy * share };
        if (gesture.mode === 'none') return;
        this.dialog.classList.add('kv-lightbox--dragging');
      }

      const dx = point.x - gesture.start.x;
      const dy = point.y - gesture.start.y;

      if (gesture.mode === 'swipe') {
        this.offset = gesture.startOffset + this.withEnds(dx, gesture.startOffset);
        this.placeTrack();
      } else if (gesture.mode === 'pan') {
        const b = this.bounds(slide, slide.scale);
        const resist = (value, min, max) => {
          if (value < min) return min - (min - value) * RESISTANCE;
          if (value > max) return max + (value - max) * RESISTANCE;
          return value;
        };
        slide.x = resist(gesture.startX + dx, b.minX, b.maxX);
        slide.y = resist(gesture.startY + dy, b.minY, b.maxY);
        this.applyZoom(slide);
      } else if (gesture.mode === 'dismiss') {
        slide.y = gesture.startY + dy;
        this.applyZoom(slide);
        const opacity = this.dismissOpacity(slide);
        this.backdrop.style.opacity = String(opacity);
        this.setUiHidden(this.uiHidden || opacity < CONTROLS_OPACITY, true);
      }
    }

    // In a set that does not wrap, dragging towards a missing neighbour moves
    // the strip by only a share of the pointer's movement. Only the stretch
    // past the rest position is held back, so a drag that starts off rest
    // does not jump as it crosses it.
    withEnds(dx, startOffset) {
      if (this.loops) return dx;
      const past = (position) =>
        this.count < 2 || (this.index === 0 && position > 0) || (this.index === this.count - 1 && position < 0);
      const shape = (position) => (past(position) ? position * RESISTANCE : position);
      return shape(startOffset + dx) - shape(startOffset);
    }

    /*
      Two fingers: the photo follows their spread and their middle. Below its
      fitted size and above its largest it follows less and less. A pinch that
      starts at the fitted size and only ever shrinks fades the backdrop, and
      closes the viewer if it is let go that way.
    */
    movePinch(slide) {
      const gesture = this.gesture;
      const points = Array.from(this.pointers.values());
      if (points.length < 2) return;
      const [a, b] = points;
      const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const max = this.maxScale(slide);
      const raw = gesture.scale * (distance / gesture.distance);
      if (raw > 1 + 1 / 15) gesture.wentOver = true;

      let scale = raw;
      gesture.closing = false;
      if (raw < 1) {
        if (!gesture.wentOver && gesture.scale <= 1.01) {
          gesture.closing = true;
          this.backdrop.style.opacity = String(clamp(1 - (1 - raw) / (1 / 1.2), 0, 1));
        } else {
          scale = 1 - Math.min((1 - raw) / 1, 1) / 3;
        }
      } else if (raw > max) {
        scale = max + Math.min((raw - max) / 6, 1);
      }
      if (!gesture.closing) this.backdrop.style.opacity = '';

      const factor = scale / gesture.scale;
      slide.scale = scale;
      slide.x = middle.x - (gesture.middle.x - gesture.startX) * factor;
      slide.y = middle.y - (gesture.middle.y - gesture.startY) * factor;
      this.applyZoom(slide);
    }

    /*
      A pointer lifts, or the browser takes it away (cancelled). A gesture the
      browser cancels is undone rather than completed: the strip, the photo
      and the backdrop go back to where they were, and nothing turns the page
      or closes.
    */
    onPointerUp(event, cancelled) {
      if (this.ignored.delete(event.pointerId)) return;
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      const gesture = this.gesture;
      if (!gesture) return;
      const slide = this.current;

      if (gesture.mode === 'pinch') {
        // The first finger to lift ends the pinch. A finger still down then
        // does nothing more until it lifts too, so it cannot tug the photo
        // while it settles.
        this.endPinch(slide, gesture, cancelled);
        if (this.pointers.size && this.state === 'open') {
          const [rest] = Array.from(this.pointers.values());
          this.gesture = this.newGesture(rest, event.timeStamp, 'touch');
          this.gesture.mode = 'none';
        }
        return;
      }

      if (this.pointers.size) return;
      const end = this.point(event);
      // The release point counts as the drag's last position: a pointer can
      // be let go further on than its last reported move.
      if (!cancelled && gesture.mode !== 'pending') {
        this.pointers.set(event.pointerId, end);
        this.onPointerMove(event);
        this.pointers.delete(event.pointerId);
      }
      this.gesture = null;
      this.dialog.classList.remove('kv-lightbox--dragging');

      switch (gesture.mode) {
        case 'pending':
        case 'none': {
          // A press that caught the strip or the photo on its way and let go
          // without dragging: everything goes on to where it was heading. A
          // tap counts where it landed on the photo as seen at that moment.
          const seen = this.offset;
          this.restAll();
          if (gesture.mode === 'pending' && !cancelled) this.onTap({ x: end.x - seen, y: end.y }, gesture.type);
          break;
        }
        case 'swipe':
          if (cancelled) {
            this.settleTrack(0, this.paced(333), EASE_SETTLE);
            this.restPhoto();
          } else {
            this.endSwipe(gesture, end, event.timeStamp);
          }
          break;
        case 'pan':
          this.endPan(slide, cancelled ? null : this.flick(gesture, end, event.timeStamp));
          break;
        case 'dismiss':
          this.endDismiss(slide, cancelled);
          break;
        default:
          break;
      }
      // A strip that the press caught off its rest position, and that this
      // gesture did not send anywhere, settles on the photo on show; a
      // backdrop caught on its way back to full strength goes the rest of the
      // way.
      if (this.state === 'open' && this.offset !== 0 && this.pendingStep === undefined) {
        this.settleTrack(0, this.paced(333), EASE_SETTLE);
      }
      this.restBackdrop();
      this.refitWaiting();
    }

    /*
      Brings back to rest whatever a press stopped on its way: the strip to
      the photo on show, that photo into its place and the backdrop to full
      strength.
    */
    restAll() {
      if (this.state !== 'open') return;
      if (this.offset !== 0 && this.pendingStep === undefined) this.settleTrack(0, this.paced(333), EASE_SETTLE);
      this.restPhoto();
      this.restBackdrop();
    }

    // The photo on show back into place: at its fitted size and position, or,
    // when enlarged, within its largest size and inside its bounds.
    restPhoto() {
      const slide = this.current;
      if (this.state !== 'open' || !slide.fit) return;
      const max = this.maxScale(slide);
      if (slide.scale > max) {
        this.zoomAround(slide, max, { x: this.width / 2, y: this.height / 2 }, this.paced(200), EASE_SETTLE);
      } else if (slide.scale <= 1.01) {
        if (slide.scale !== 1 || slide.x !== slide.fit.x || slide.y !== slide.fit.y) {
          this.moveSlide(slide, { scale: 1, x: slide.fit.x, y: slide.fit.y }, this.paced(300), EASE_SETTLE);
        }
      } else {
        const pan = this.clampPan(slide, slide.scale, slide.x, slide.y);
        if (pan.x !== slide.x || pan.y !== slide.y) this.moveSlide(slide, pan, this.paced(300), EASE_BOUNCE);
      }
      this.updateZoomState();
    }

    // A backdrop left part-faded by a stopped movement fades back in.
    restBackdrop() {
      if (this.state !== 'open' || !this.backdrop.style.opacity) return;
      const opacity = px(this.backdrop.style.opacity, 1);
      this.backdrop.style.opacity = '';
      this.animate(this.backdrop, [{ opacity }, { opacity: 1 }], this.paced(300), EASE_SETTLE);
    }

    /*
      The last stretch of a drag, per axis: from the sample before last to the
      release, over the time since the last sample plus one sampling gap.
    */
    flick(gesture, end, time) {
      const samples = gesture.samples;
      const before = samples.length > 1 ? samples[samples.length - 2] : gesture.origin;
      const since = samples.length > 1 ? gesture.sampleTime : gesture.startTime;
      const duration = Math.max(time - since, 0) + SAMPLE_TIME;
      const axis = (offset) => {
        const distance = Math.abs(offset);
        let speed = distance > FLICK_ON ? offset / duration : 0;
        if (Math.abs(speed) < FLICK_MIN_SPEED) speed = 0;
        return { offset, distance, speed };
      };
      return { x: axis(end.x - before.x), y: axis(end.y - before.y) };
    }

    endSwipe(gesture, end, time) {
      const total = end.x - gesture.origin.x;
      const flick = this.flick(gesture, end, time).x;
      const still = flick.distance < FLICK_STILL;
      let direction = 0;
      if (total > SWIPE_MIN && (still || flick.offset > FLICK_ON)) direction = -1;
      else if (total < -SWIPE_MIN && (still || flick.offset < -FLICK_ON)) direction = 1;
      if (direction && this.neighbour(direction) < 0) direction = 0;

      const target = -direction * this.pitch;
      const distance = Math.abs(target - this.offset);
      let duration;
      if (!direction && target > this.offset !== flick.speed > 0) {
        duration = 333;
      } else {
        duration = flick.speed ? distance / Math.abs(flick.speed) : 333;
        duration = clamp(duration, SETTLE_MIN, SETTLE_MAX);
      }
      this.settleTrack(direction, this.paced(duration), EASE_SETTLE);
      // Staying on this photo, it also goes back into place if the press that
      // began the drag caught it on its way somewhere.
      if (!direction) this.restPhoto();
    }

    // An enlarged photo let go: it carries on with the flick, if there was
    // one, and comes to rest inside its bounds.
    endPan(slide, flick) {
      const speedX = flick ? flick.x.speed : 0;
      const speedY = flick ? flick.y.speed : 0;
      const carry = speedX || speedY ? PAN_CARRY : 0;
      const pan = this.clampPan(slide, slide.scale, slide.x + speedX * carry, slide.y + speedY * carry);
      this.moveSlide(slide, pan, this.paced(300), EASE_BOUNCE);
    }

    // The backdrop's strength while a photo is dragged up or down: full at
    // rest, gone once it has travelled half the screen's height.
    dismissOpacity(slide) {
      const moved = slide.y - slide.fit.y;
      return clamp(1 - Math.abs(moved) / (this.height / 2), 0, 1);
    }

    endDismiss(slide, cancelled) {
      if (!cancelled && this.dismissOpacity(slide) < CLOSE_OPACITY) {
        this.close();
        return;
      }
      const opacity = px(this.backdrop.style.opacity, 1);
      this.backdrop.style.opacity = '';
      this.setUiHidden(this.uiHidden);
      const duration = this.paced(300);
      this.moveSlide(slide, { scale: 1, x: slide.fit.x, y: slide.fit.y }, duration, EASE_SETTLE);
      this.animate(this.backdrop, [{ opacity }, { opacity: 1 }], duration, EASE_SETTLE);
    }

    endPinch(slide, gesture, cancelled) {
      this.gesture = null;
      this.dialog.classList.remove('kv-lightbox--dragging');
      if (!cancelled && gesture.closing && slide.scale < 1) {
        this.close();
        return;
      }
      // The controls come back to what the shopper last chose, whatever a
      // drag before the pinch did to them.
      this.setUiHidden(this.uiHidden);
      const opacity = px(this.backdrop.style.opacity, 1);
      this.backdrop.style.opacity = '';
      const scale = clamp(slide.scale, 1, this.maxScale(slide));
      const middle = { x: this.width / 2, y: this.height / 2 };
      const duration = this.paced(200);
      this.zoomAround(slide, scale, scale === slide.scale ? { x: slide.x, y: slide.y } : middle, duration, EASE_SETTLE);
      if (opacity < 1) this.animate(this.backdrop, [{ opacity }, { opacity: 1 }], duration, EASE_SETTLE);
    }

    // Whether a point on the screen falls on the photo on show.
    onPhoto(point) {
      const slide = this.current;
      if (!slide.fit) return false;
      const width = slide.fit.width * slide.scale;
      const height = slide.fit.height * slide.scale;
      return point.x >= slide.x && point.x <= slide.x + width && point.y >= slide.y && point.y <= slide.y + height;
    }

    /*
      A press that did not move. With a mouse: on the photo it zooms in or out
      (a photo with nothing more to show closes instead), beside it it closes.
      On a touch screen a single tap hides or shows the controls and a double
      tap zooms - or, when the store asks for it, a single tap on the photo
      zooms straight away.
    */
    onTap(point, pointerType) {
      if (pointerType === 'mouse') {
        if (!this.onPhoto(point)) {
          this.close();
          return;
        }
        const slide = this.current;
        if (slide.scale > 1.01 || this.zoomable(slide)) this.toggleZoom(point, 'mouse');
        else this.close();
        return;
      }

      // On a touch screen the controls never fade on their own. A tap that
      // finds them faded - after a key press or a mouse on the same device -
      // brings them back and stops them fading again until the next one. It
      // still counts as the first of a double tap.
      const revealed = this.dialog.classList.contains('kv-lightbox--idle');
      if (revealed) this.reveal();

      if (this.tapZoom === 'single') {
        if (this.onPhoto(point)) this.toggleZoom(point, pointerType);
        else if (!revealed) this.setUiHidden(!this.uiHidden);
        return;
      }

      const previous = this.lastTap;
      if (
        previous &&
        performance.now() - previous.time < DOUBLE_TAP_TIME &&
        Math.abs(point.x - previous.x) < DOUBLE_TAP_DISTANCE &&
        Math.abs(point.y - previous.y) < DOUBLE_TAP_DISTANCE
      ) {
        this.cancelTap();
        this.toggleZoom(point, pointerType);
        return;
      }
      this.cancelTap();
      this.lastTap = { x: point.x, y: point.y, time: performance.now() };
      // A tap that has just brought the controls back does not hide them again.
      if (revealed) return;
      this.tapTimer = setTimeout(() => {
        this.lastTap = null;
        this.setUiHidden(!this.uiHidden);
      }, DOUBLE_TAP_TIME);
    }

    cancelTap() {
      clearTimeout(this.tapTimer);
      this.tapTimer = 0;
      this.lastTap = null;
    }

    releaseAll() {
      this.pointers.clear();
      this.ignored.clear();
      this.gesture = null;
      this.dialog.classList.remove('kv-lightbox--dragging');
    }

    /*
      The wheel. Over a photo at its fitted size a real turn closes the viewer,
      as scrolling away from a photo would. Over an enlarged photo it moves the
      photo about. A pinch on a touchpad arrives as a wheel with Ctrl held and
      zooms around the pointer. The page behind never moves.
    */
    onWheel(event) {
      event.preventDefault();
      if (this.state !== 'open') return;
      const slide = this.current;
      if (!slide.fit) return;
      if (!event.ctrlKey && slide.scale <= 1.01) {
        if (Math.abs(event.deltaY) > 2) this.close();
        return;
      }
      // The wheel moves the photo by steps rather than by following a
      // pointer, so anything still moving simply finishes at once first.
      this.stopMoves(false);
      this.restBackdrop();
      if (event.ctrlKey) {
        const scale = clamp(slide.scale * Math.exp(-event.deltaY * 0.01), 1, this.maxScale(slide));
        this.zoomAround(slide, scale, this.point(event), 0);
        return;
      }
      const lines = event.deltaMode === 1 ? 18 : 1;
      const pan = this.clampPan(slide, slide.scale, slide.x - event.deltaX * lines, slide.y - event.deltaY * lines);
      this.moveSlide(slide, pan, 0);
    }

    /* ---------------------------------------------------------------------
       Controls
       --------------------------------------------------------------------- */

    // Hides or shows the bar and the arrows. A drag that hides them only for
    // its own length does not change what a tap will do next.
    setUiHidden(hidden, passing) {
      if (!passing) this.uiHidden = hidden;
      this.dialog.classList.toggle('kv-lightbox--ui-hidden', hidden);
    }

    // A touch that finds the controls faded brings them back, and they stay
    // until a mouse or a key starts their fading again.
    reveal() {
      clearInterval(this.idleTimer);
      this.idleTimer = 0;
      this.idleCount = 0;
      this.dialog.classList.remove('kv-lightbox--idle');
      this.setUiHidden(false);
    }

    // The mouse moved or a key was pressed: the controls come back, and fade
    // again two to four seconds after the last of these - never while the
    // keyboard is on one of them.
    wake() {
      this.dialog.classList.remove('kv-lightbox--idle');
      this.idleCount = 0;
      if (this.idleTimer || this.state === 'closed') return;
      this.idleTimer = setInterval(() => {
        if (this.state === 'closed') {
          clearInterval(this.idleTimer);
          this.idleTimer = 0;
          return;
        }
        const active = document.activeElement;
        if (active && active !== this.dialog && this.dialog.contains(active) && active.matches(':focus-visible')) {
          this.idleCount = 0;
          return;
        }
        this.idleCount += 1;
        if (this.idleCount === 2) this.dialog.classList.add('kv-lightbox--idle');
      }, IDLE_STEP);
    }

    /*
      Fades the bar and the arrows between none and their resting strength.
      Each is left at the strength it fades to, so none of them shows again
      for a moment once the fade is over.
    */
    animateControls(from, to, duration) {
      return [this.bar, ...this.navs].map((element) => {
        if (!element) return null;
        const resting = px(getComputedStyle(element).opacity, 1);
        element.style.opacity = to ? '' : '0';
        return this.animate(element, [{ opacity: from * resting }, { opacity: to * resting }], duration);
      });
    }

    onKeydown(event) {
      if (this.state === 'closed') return;
      // Whatever the key, the controls come back so the keyboard never lands
      // on a button that cannot be seen.
      this.wake();
      if (this.uiHidden) this.setUiHidden(false);
      if (this.state !== 'open' || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
      if (!direction) return;
      event.preventDefault();
      // A key held down repeats faster than a slide plays. Each repeat that
      // comes while the strip is still sliding is let go, so the held key
      // plays one whole slide after another instead of cutting between them.
      if (event.repeat && this.pendingStep !== undefined) return;
      this.go(direction);
    }

    // A new window size lays everything out again at the fitted size. Not on
    // the way out: the photo is already shrinking back into the page.
    onResize() {
      if (this.state === 'closed' || this.state === 'closing') return;
      this.stopMoves(false);
      this.measure();
      this.offset = 0;
      this.placeTrack();
      this.slides.forEach((slide) => {
        if (slide.item >= 0) this.fit(slide);
      });
      this.fillAll();
    }

    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      } else if (this.frame && this.frame.requestFullscreen) {
        this.frame.requestFullscreen().catch(() => {});
      }
    }

    share() {
      const link = this.items[this.index];
      const url = link && link.href;
      if (!url) return;

      if (navigator.share) {
        navigator.share({ url }).catch(() => {});
        return;
      }

      // No share sheet on this device, so the link goes to the clipboard and
      // the viewer says so rather than appearing to do nothing.
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(url)
          .then(() => this.announce(this.texts.copied))
          .catch(() => this.announce(this.texts.notCopied));
      }
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    emit(name) {
      if (!this.group) return;
      this.group.dispatchEvent(
        new CustomEvent(name, { bubbles: true, detail: { index: this.index, link: this.items[this.index] } })
      );
    }

    /* ---------------------------------------------------------------------
       Movement
       --------------------------------------------------------------------- */

    /*
      Plays a movement on an element whose end position has already been
      written to it, so the movement only ever shows the way there. Each one is
      kept until it is over, so a new press can stop it where it is.

      The entry returned carries a promise, over, that settles however the
      movement ends. Should the browser never report the end, a timer ends it:
      counted from the movement's first frame, so a first frame that comes
      late does not cut it short, and with a longer wait where no frame comes
      at all.
    */
    animate(element, keyframes, duration, easing, kind) {
      if (!element || !duration || typeof element.animate !== 'function') return null;
      const animation = element.animate(keyframes, { duration, easing: easing || EASE });
      let settle = null;
      const entry = {
        animation,
        element,
        kind: kind || 'other',
        done: false,
        over: new Promise((resolve) => {
          settle = resolve;
        })
      };
      entry.end = () => {
        if (entry.done) return;
        entry.done = true;
        this.moves.delete(entry);
        settle();
        // Photos whose files arrived while something was moving are fitted
        // once everything has come to rest.
        if (!this.moves.size) Promise.resolve().then(() => this.refitWaiting());
      };
      this.moves.add(entry);

      animation.finished.then(entry.end, entry.end);
      const stop = () => {
        if (entry.done) return;
        animation.cancel();
        entry.end();
      };
      animation.ready.then(() => setTimeout(stop, duration + MOVE_GRACE), () => {});
      setTimeout(() => {
        if (animation.pending) stop();
      }, duration + MOVE_NO_FRAMES);
      return entry;
    }

    cancelMoves(kind) {
      this.moves.forEach((entry) => {
        if (kind && entry.kind !== kind) return;
        entry.animation.cancel();
        entry.end();
      });
    }

    /*
      Stops every movement. Frozen (true) keeps each element where the
      movement had got to, so a press mid-way takes the photo from where it is
      seen; otherwise elements jump to their end positions. Quiet is handed on
      to a step between photos that has to be completed, and keeps the counter
      as it is when one is taken back.
    */
    stopMoves(frozen, quiet) {
      if (!this.moves.size && this.pendingStep === undefined) return;
      const trackMoving = Array.from(this.moves).some((entry) => entry.kind === 'track');
      const zoomMoving = new Set();

      if (frozen) {
        this.moves.forEach((entry) => {
          const style = getComputedStyle(entry.element);
          if (entry.element === this.track) return;
          if (entry.element.classList.contains('kv-lightbox__zoom')) {
            zoomMoving.add(entry.element);
            entry.element.style.clipPath = style.clipPath === 'none' ? '' : style.clipPath;
          }
          // The backdrop and the controls keep the strength they had reached,
          // so a fade that follows starts from what is seen.
          if (entry.element === this.backdrop || entry.element === this.bar || this.navs.includes(entry.element)) {
            // The controls' own fade from the stylesheet is held back too, so
            // it cannot start from their resting strength instead.
            if (entry.element !== this.backdrop) entry.element.style.transition = 'none';
            entry.element.style.opacity = style.opacity;
          }
        });
        if (trackMoving) this.offset = this.seenOffset();
        // A photo stopped mid-zoom keeps the size and place it had reached.
        this.slides.forEach((slide) => {
          if (!zoomMoving.has(slide.zoom)) return;
          const matrix = new DOMMatrixReadOnly(getComputedStyle(slide.zoom).transform);
          if (!slide.fit || !matrix.a) return;
          slide.scale = matrix.a;
          slide.x = matrix.m41;
          slide.y = matrix.m42;
        });
      }
      this.cancelMoves();
      this.slides.forEach((slide) => this.applyZoom(slide));

      // A step between photos that was under way - or a settle back to the
      // same photo - is completed at once; the strip keeps what is on screen
      // by carrying its position along, never more than one photo's width.
      // A step stopped before the strip has passed the photo it is leaving
      // is taken back instead: the photo it was heading for has not come
      // into sight, and the strip stays where it is seen, on its way back to
      // the photo it had not yet left. Presses waiting for the step go with
      // it either way.
      if (this.pendingStep !== undefined) {
        const direction = this.pendingStep;
        const offset = this.offset;
        this.queued = 0;
        if (frozen && this.overruns()) {
          this.index = (this.index - direction + this.count) % this.count;
          this.dropStep();
          this.placeTrack();
          if (!quiet) this.updateCounter();
        } else {
          this.commitStep(quiet, frozen && direction !== 0);
          if (frozen) {
            this.offset = clamp(offset + direction * this.pitch, -this.pitch, this.pitch);
            this.placeTrack();
            if (!quiet && direction !== 0) this.fillSeenSide();
          }
        }
      } else if (frozen && trackMoving) {
        this.placeTrack();
      }
      this.updateZoomState();
    }

    /*
      With the strip carried off its rest position, the frame on the side now
      in sight shows its photo - even in a set of two, where a frame past
      either end is normally left empty - so no empty stretch appears.
    */
    fillSeenSide() {
      if (this.count < 2 || !this.offset) return;
      const direction = this.offset > 0 ? -1 : 1;
      const slide = this.slides[1 + direction];
      if (!slide.el.hidden) return;
      slide.el.hidden = false;
      this.fill(slide, (this.index + direction + this.count) % this.count);
    }
  }

  const viewers = new WeakMap();

  /*
    A group opens in the viewer rendered by its own section, so a section can
    restyle its popup without changing the popup of any other section on the
    same page. A group whose section renders no viewer uses the first one on
    the page.

    Each dialog keeps one instance between opens. The dialog is looked up on
    every click, so a section reloaded in the theme editor, which replaces its
    markup, gets a fresh instance instead of one bound to a removed dialog.
  */
  const getViewer = (link) => {
    const section = link.closest('.shopify-section');
    const dialog =
      (section && section.querySelector('[data-kv-lightbox]')) || document.querySelector('[data-kv-lightbox]');
    if (!dialog) return null;

    if (!viewers.has(dialog)) viewers.set(dialog, new KvLightbox(dialog));
    return viewers.get(dialog);
  };

  // One listener on the document, so groups rendered later - a section reloaded
  // in the theme editor, a gallery swapped by a variant change - work without
  // being registered.
  document.addEventListener('click', (event) => {
    const link = event.target.closest(`${GROUP} ${ITEM}`);
    if (!link) return;

    // Leave the modified clicks alone: they mean "open this elsewhere".
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const instance = getViewer(link);
    if (!instance) return;

    const group = link.closest(GROUP);
    const items = Array.from(group.querySelectorAll(ITEM));

    event.preventDefault();
    instance.open(items, items.indexOf(link), group);
  });
})();
